import React, { useMemo, useState } from 'react';
import { Alert, Stack, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CustomPropertyStatusField from './CustomPropertyStatusField';
import MlsPropertyStatusField from './MlsPropertyStatusField';
import { ListingDateField } from './listingModalUi';
import { formatDaysOnMarket } from './ListingsSummarySection';
import {
  computePreviewDaysOnMarket,
  parseDateInput,
  requiresOffMarketDate,
} from './listingMarketDates';

/** Shared status section for link + edit modals. */
const ListingStatusFields = ({
  theme,
  compact = false,
  mlsSyncEnabled = false,
  mlsStatus = 'unknown',
  customPropertyStatus = '',
  onCustomPropertyStatusChange,
  onMarketDate = '',
  onOffMarketDate = '',
  onOnMarketDateChange,
  onOffMarketDateChange,
  disabled = false,
  requireOnMarketDate = false,
  showDaysPreview = false,
}) => {
  const manualDisabled = disabled || mlsSyncEnabled;
  const showOffMarketDate = requiresOffMarketDate(customPropertyStatus);
  const [openDateField, setOpenDateField] = useState(null);

  const previewDays = useMemo(() => {
    if (!showDaysPreview || mlsSyncEnabled) return null;
    const onIso = parseDateInput(onMarketDate);
    const offIso = showOffMarketDate ? parseDateInput(onOffMarketDate) : null;
    if (!onIso) return null;
    return computePreviewDaysOnMarket({
      onMarketDate: onIso,
      offMarketDate: offIso,
      customPropertyStatus: customPropertyStatus || null,
    });
  }, [
    showDaysPreview,
    mlsSyncEnabled,
    onMarketDate,
    onOffMarketDate,
    customPropertyStatus,
    showOffMarketDate,
  ]);

  const showMlsStatusField = mlsSyncEnabled || (mlsStatus && mlsStatus !== 'unknown');

  return (
    <Stack spacing={2}>
      {showMlsStatusField && (
        <MlsPropertyStatusField
          theme={theme}
          value={mlsStatus}
          mlsSyncEnabled={mlsSyncEnabled}
        />
      )}

      {mlsSyncEnabled ? (
        <Alert severity="info" sx={{ fontSize: '0.78rem', py: 0.5 }}>
          MLS updates are on for this listing. Status and days on market will come from your MLS
          feed — manual entry is paused.
        </Alert>
      ) : (
        <>
          {!compact ? (
            <>
              <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(0,0,0,0.55)', lineHeight: 1.5 }}>
                {requireOnMarketDate
                  ? 'Set the property status and on-market date below.'
                  : 'You can add a property status now, or come back later from Manage listing.'}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.45)', lineHeight: 1.45 }}>
                {requireOnMarketDate
                  ? 'On-market date is required for days on market.'
                  : 'On-market date is optional when linking. We will ask for it when you save the status.'}
                {' '}
                For under contract or sold, add an off-market date — days on market stops there.
              </Typography>
            </>
          ) : null}

          <CustomPropertyStatusField
            value={customPropertyStatus}
            onChange={onCustomPropertyStatusChange}
            disabled={manualDisabled}
            label="Property status"
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={2}>
              <ListingDateField
                label="On-market date"
                value={onMarketDate}
                onChange={onOnMarketDateChange}
                disabled={manualDisabled}
                required={requireOnMarketDate}
                open={openDateField === 'on'}
                onOpenChange={(next) => setOpenDateField(next ? 'on' : null)}
                helperText={
                  compact
                    ? undefined
                    : requireOnMarketDate
                      ? 'Required for days on market.'
                      : 'Optional now — required when you save the status later.'
                }
              />

              {showOffMarketDate ? (
                <ListingDateField
                  label="Off-market date"
                  value={onOffMarketDate}
                  onChange={onOffMarketDateChange}
                  disabled={manualDisabled}
                  required
                  open={openDateField === 'off'}
                  onOpenChange={(next) => setOpenDateField(next ? 'off' : null)}
                  helperText={compact ? undefined : 'Required for under contract or sold.'}
                />
              ) : null}
            </Stack>
          </LocalizationProvider>

          {previewDays != null ? (
            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.55)' }}>
              Days on market:{' '}
              <Typography component="span" sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.78)' }}>
                {formatDaysOnMarket(previewDays)}
              </Typography>
            </Typography>
          ) : null}
        </>
      )}
    </Stack>
  );
};

export default ListingStatusFields;
